// Non-visual logic for the projected-quote-lines drill-down — the table behind
// Month Landing's "Projected, not yet booked" band.
//
// The band is TWO projection layers (`projected_weighted_estimates` plus
// `projected_unquoted_baseline`), and only the first has quotes behind it. So
// the panel must say which part of the band the table accounts for; a reader
// who finds a gap between the band and the table's total, with nothing naming
// it, stops trusting both. `unquotedBaseline` is that difference.
//
// See docs/features/month-landing-forecast/35_quote_lines_drilldown_open_questions.md.

import type {
  MonthLandingQuoteLine,
  MonthLandingQuoteLinesResponse,
} from "../data/api-types";
import type { MonthLandingMetric } from "./month-landing";

/** The money on a line for the metric the page is showing. */
export function quoteLineAmount(
  line: MonthLandingQuoteLine,
  metric: MonthLandingMetric,
): number {
  return metric === "contribution"
    ? line.weighted_contribution
    : line.weighted_revenue;
}

/** The served total for the metric the page is showing — the sum of the lines,
 *  computed server-side from the rows it shipped. */
export function quoteLinesTotal(
  response: MonthLandingQuoteLinesResponse,
  metric: MonthLandingMetric,
): number {
  return metric === "contribution"
    ? response.total_weighted_contribution
    : response.total_weighted_revenue;
}

/**
 * The part of the band no quote explains — the unquoted baseline layer.
 *
 * Floored at zero: the band is itself a residual of the displayed landing, so
 * a filter or a rounding edge could make it fractionally the smaller of the
 * two, and a negative "baseline" is a worse lie than a zero.
 */
export function unquotedBaseline(band: number, quotesTotal: number): number {
  return Math.max(0, band - quotesTotal);
}

/**
 * Each quote's whole weighted value inside the month, keyed by quote.
 *
 * One quote legitimately occupies several rows: the win-rate curve spreads its
 * remaining conversion over the days its increments land. The per-day figure is
 * what the chart draws, but the question a rep actually asks is "how much is
 * still expected from this quote" — so the table carries the quote's month
 * total on every one of its rows, and a repeated quote number reads as one
 * quote spread out rather than as several.
 */
export function quoteTotalsThisMonth(
  lines: readonly MonthLandingQuoteLine[],
  metric: MonthLandingMetric,
): Map<string, number> {
  const totals = new Map<string, number>();
  for (const line of lines) {
    totals.set(
      line.estimate_id,
      (totals.get(line.estimate_id) ?? 0) + quoteLineAmount(line, metric),
    );
  }
  return totals;
}

/** Distinct quotes behind the table — the count that makes "47 rows" legible. */
export function distinctQuoteCount(
  lines: readonly MonthLandingQuoteLine[],
): number {
  return new Set(lines.map((l) => l.estimate_id)).size;
}

const CSV_HEADER = [
  "Quote",
  "Customer",
  "Expected date",
  "Weighted value",
  "Weighted contribution",
  "Quote total this month (value)",
  "Quote total this month (contribution)",
  "CSR",
  "Sales person",
] as const;

/** Quote a CSV field whenever it could otherwise break the row. */
function csvField(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The whole population as CSV — every row, not the on-screen cap.
 *
 * Both metrics ship in every row rather than only the displayed one: the file
 * outlives the toggle that was set when it was downloaded, and a column whose
 * meaning depends on a UI state nobody recorded is a reconciliation trap.
 * Amounts are raw numbers, not formatted currency, so the file arithmetises.
 */
export function quoteLinesToCsv(
  lines: readonly MonthLandingQuoteLine[],
): string {
  const revenueTotals = quoteTotalsThisMonth(lines, "revenue");
  const contributionTotals = quoteTotalsThisMonth(lines, "contribution");
  const rows = [CSV_HEADER.join(",")];
  for (const line of lines) {
    rows.push(
      [
        line.reference,
        line.customer_name,
        line.expected_date,
        line.weighted_revenue,
        line.weighted_contribution,
        revenueTotals.get(line.estimate_id) ?? line.weighted_revenue,
        contributionTotals.get(line.estimate_id) ?? line.weighted_contribution,
        line.csr_name,
        line.rep_name,
      ]
        .map(csvField)
        .join(","),
    );
  }
  return rows.join("\n") + "\n";
}

/** `month-landing-quotes-2026-10-company.csv` — month and scope in the name,
 *  because two downloads otherwise overwrite each other in Downloads. */
export function quoteLinesCsvFilename(month: string, scope: string): string {
  return `month-landing-quotes-${month}-${scope.replace(/[^a-zA-Z0-9]+/g, "-")}.csv`;
}
